import { ApiError, currentUserId } from "../api/client";
import { activeExam, type ProfileExam } from "./activeExam";

/**
 * The last good answer to a read, kept on the device so a screen can show it
 * when the network is gone.
 *
 * Only reads that are safe to show stale go through here: the student's own
 * profile, subjects and readiness, a subject's years and topics. Nothing here
 * grants anything — entitlement and payment are still decided by the server on
 * every request that does something, so a stale "full access" can only be
 * *displayed*, never used to unlock a paid action.
 *
 * Keyed by the signed-in student. Phones are shared in families, and one
 * student must never see another's readiness because they last opened the app
 * on the same phone. Cleared on sign-out.
 *
 * And by exam profile: a student sitting JAMB and WAEC has a readiness for
 * each, and the WAEC screen must never paint the JAMB one while offline.
 *
 * `localStorage`, not IndexedDB: each entry is a few kilobytes of JSON, read
 * synchronously on the first render so an offline screen paints at once.
 */
const PREFIX = "prepai.cache.";

interface Entry<T> {
  owner: string;
  savedAt: number;
  data: T;
}

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function key(name: string, exam: ProfileExam | null = activeExam()): string {
  return exam ? `${PREFIX}${exam}.${name}` : `${PREFIX}${name}`;
}

/** What was last saved under `name` for the student signed in now, with when. */
export function readCache<T>(name: string): { data: T; savedAt: number } | null {
  const owner = currentUserId();
  if (!owner) return null;
  try {
    const raw = store()?.getItem(key(name));
    if (!raw) return null;
    const entry = JSON.parse(raw) as Entry<T>;
    // Someone else's on a shared phone: as good as not there.
    if (entry.owner !== owner) return null;
    return { data: entry.data, savedAt: entry.savedAt };
  } catch {
    return null;
  }
}

/**
 * Save `data` as the last good answer. `exam` is the profile it was asked
 * for — by default the one shown now — so a slow answer that lands after a
 * switch is filed under the exam it belongs to, not the new one.
 */
export function writeCache<T>(
  name: string,
  data: T,
  exam: ProfileExam | null = activeExam(),
): void {
  const owner = currentUserId();
  if (!owner) return;
  try {
    const entry: Entry<T> = { owner, savedAt: Date.now(), data };
    store()?.setItem(key(name, exam), JSON.stringify(entry));
  } catch {
    /* Full or disabled: the screen works online, it just has nothing to fall back on. */
  }
}

/** Forget everything cached. Run on sign-out. */
export function clearCache(): void {
  const s = store();
  if (!s) return;
  try {
    const names: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k?.startsWith(PREFIX)) names.push(k);
    }
    for (const k of names) s.removeItem(k);
  } catch {
    /* storage disabled */
  }
}

/**
 * True when an error means "no connection", not "the server said no".
 * A 401 or a 500 is a real answer and must not be papered over with old data.
 */
export function isNetworkFailure(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0;
}

/**
 * Fetch, saving the answer; on a network failure, fall back to the last saved
 * copy. Rejects as before when there is no copy, or when the server answered
 * with an error.
 */
export async function cached<T>(
  name: string,
  load: () => Promise<T>,
): Promise<{ data: T; savedAt: number | null }> {
  const exam = activeExam();
  try {
    const data = await load();
    writeCache(name, data, exam);
    return { data, savedAt: null };
  } catch (error) {
    if (!isNetworkFailure(error)) throw error;
    const hit = readCache<T>(name);
    if (!hit) throw error;
    return hit;
  }
}
